import * as Const from '../../shared/constants';

import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

// interfaces
import { IStock } from 'src/app/interfaces/stock';

// servicios
import { StockService } from 'src/app/services/datos/stock.service';
import { UsuarioService } from 'src/app/services/datos/usuario.service';
import { NotificacionesService } from 'src/app/services/notificaciones.service';
import { AlmacenesService } from 'src/app/services/datos/almacenes.service';

// librerías
import FuncLib from 'src/app/providers/funcionesLib';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-stock-mover-articulo-pda',
  templateUrl: './stock-mover-articulo.component.html',
  styleUrls: ['./stock-mover-articulo.component.scss']
})
export class StockMoverArticuloPdaComponent implements OnInit {

  // Constantes
  Const = Const;

  // ViewChilds para foco automático
  @ViewChild('txtUbiOrigen') txtUbiOrigen!: ElementRef;
  @ViewChild('txtEan') txtEan!: ElementRef;
  @ViewChild('txtCantidad') txtCantidad!: ElementRef;
  @ViewChild('txtUbiDestino') txtUbiDestino!: ElementRef;

  // Formulario reactivo
  public moverForm: FormGroup = new FormGroup({});

  // Estados de carga
  public loadingOrigen: boolean = false;
  public loadingDestino: boolean = false;
  public loadingGuardar: boolean = false;

  // Validaciones de paso
  public ubiOrigenValida: boolean = false;
  public ubiDestinoValida: boolean = false;
  public enviado: boolean = false;

  // Listados de stock
  public stockOrigenList: IStock[] = [];
  public stockDestinoList: IStock[] = [];
  public stockSelected: IStock | null = null;

  // Control de acordeón para listados de stock (colapsados por defecto)
  public mostrarStockOrigen: boolean = false;
  public mostrarStockDestino: boolean = false;

  // Datos de clientes y ubicaciones descifradas
  public clienteOrigen: string = '';
  public id_clienteOrigen: number | null = null;
  public clienteDestino: string = '';
  public codUbiOriDecript: string = '';
  public codUbiDesDecript: string = '';
  public destinoLibre: boolean = false;

  // Mensajes de error específicos
  public errorOrigen: string = '';
  public errorEan: string = '';
  public errorDestino: string = '';

  // Fotos
  public fotoDir: string = environment.fotoDir;
  public imgResized: string = localStorage.getItem('imgResized') || '/resize/';

  // Librería de funciones
  public funcLib: FuncLib = new FuncLib();

  constructor(
    private fb: FormBuilder,
    private stockSrv: StockService,
    private usuarioSrv: UsuarioService,
    private notificacionesSrv: NotificacionesService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initForm();
  }

  initForm() {
    this.moverForm = this.fb.group({
      ubiOrigen: ['', [Validators.required, Validators.minLength(12), Validators.maxLength(12)]],
      ean: ['', [Validators.required]],
      cantidad: [1, [Validators.required, Validators.min(1)]],
      ubiDestino: ['', [Validators.required, Validators.minLength(12), Validators.maxLength(12)]]
    });

    // Auto-foco inicial en origen
    setTimeout(() => {
      this.focoUbiOrigen();
    }, 300);
  }

  // =========================================================================
  // PASO 1: Ubicación de Origen
  // =========================================================================
  onUbiOrigenInput() {
    const val = this.moverForm.get('ubiOrigen')?.value || '';
    if (val.length === 12) {
      this.validarUbiOrigen();
    } else {
      this.resetDesdeOrigen();
    }
  }

  validarUbiOrigen() {
    const ubiOrigen = this.moverForm.get('ubiOrigen')?.value || '';
    if (ubiOrigen.length !== 12) {
      return;
    }

    this.loadingOrigen = true;
    this.errorOrigen = '';
    this.stockOrigenList = [];
    this.mostrarStockOrigen = false;
    this.stockSelected = null;
    this.ubiOrigenValida = false;
    this.codUbiOriDecript = '';

    this.stockSrv.getLista({ codUbi: ubiOrigen }).subscribe({
      next: (resp) => {
        this.loadingOrigen = false;

        if (resp.error) {
          this.errorOrigen = resp.mensaje || 'La ubicación de origen no existe.';
          this.moverForm.get('ubiOrigen')?.setErrors({ apiError: this.errorOrigen });
          // this.notificacionesSrv.aviso('error', this.errorOrigen);
          this.focoUbiOrigen();
          return;
        }

        if (!resp.data || resp.data.length === 0) {
          this.errorOrigen = 'La ubicación de origen no contiene ningún artículo en stock.';
          this.moverForm.get('ubiOrigen')?.setErrors({ emptyUbi: true });
          // this.notificacionesSrv.aviso('warning', this.errorOrigen);
          this.focoUbiOrigen();
          return;
        }

        // Origen válido con stock
        this.ubiOrigenValida = true;
        this.stockOrigenList = resp.data;
        this.mostrarStockOrigen = false; // Colapsado por defecto
        this.id_clienteOrigen = resp.data[0].idCliente || null;
        this.clienteOrigen = resp.data[0].clienteNombre || '';
        this.codUbiOriDecript = this.funcLib.CodUbiDecript(ubiOrigen);

        // Foco al campo EAN
        setTimeout(() => {
          this.focoEan();
        }, 250);
      },
      error: (err) => {
        this.loadingOrigen = false;
        this.errorOrigen = 'Error de conexión al verificar la ubicación de origen.';
        this.notificacionesSrv.aviso('error', this.errorOrigen);
      }
    });
  }

  // =========================================================================
  // PASO 2: EAN y Cantidad
  // =========================================================================
  onEanInput() {
    const ean = (this.moverForm.get('ean')?.value || '').trim();
    if (ean.length >= 3) {
      this.validarEan();
    }
  }

  validarEan() {
    const eanBuscado = (this.moverForm.get('ean')?.value || '').trim();
    this.errorEan = '';
    this.stockSelected = null;

    if (!eanBuscado) {
      return;
    }

    if (!this.ubiOrigenValida || this.stockOrigenList.length === 0) {
      this.errorEan = 'Primero debes leer una ubicación de origen válida.';
      this.focoUbiOrigen();
      return;
    }

    // Filtrar coincidencias en la ubicación origen por EAN
    const coincidencias = this.stockOrigenList.filter(item =>
      item.articuloEan && item.articuloEan.trim() === eanBuscado
    );

    if (coincidencias.length === 0) {
      // Comprobar si el operario introdujo una referencia/SKU en lugar del EAN
      const esReferencia = this.stockOrigenList.some(item =>
        item.articuloSku && item.articuloSku.trim().toLowerCase() === eanBuscado.toLowerCase()
      );

      if (esReferencia) {
        this.errorEan = `No se permite la búsqueda por referencia ("${eanBuscado}"). Solo se permite el escaneo o entrada del código EAN.`;
      } else {
        this.errorEan = `El artículo con código EAN "${eanBuscado}" no existe en la ubicación de origen.`;
      }

      this.moverForm.get('ean')?.setErrors({ noEncontrado: true });
      // this.notificacionesSrv.aviso('error', this.errorEan);
      this.focoEan();
      return;
    }

    // REGLA ESTRICTA: NO pueden existir múltiples lotes para el mismo EAN
    if (coincidencias.length > 1) {
      this.errorEan = `Existen múltiples lotes (${coincidencias.length}) para este EAN en la ubicación origen. No está permitido el movimiento en esta condición.`;
      this.moverForm.get('ean')?.setErrors({ multipleLotes: true });
      this.notificacionesSrv.aviso('error', this.errorEan);
      this.focoEan();
      return;
    }

    // Exactamente 1 lote disponible
    const reg = coincidencias[0];
    const maxDisponible = reg.cantidadDisponible !== undefined ? reg.cantidadDisponible : (reg.cantidad || 0);

    if (maxDisponible <= 0) {
      this.errorEan = `El artículo con EAN "${eanBuscado}" no tiene cantidad disponible para mover (Disponible: ${maxDisponible}).`;
      this.moverForm.get('ean')?.setErrors({ sinDisponible: true });
      // this.notificacionesSrv.aviso('warning', this.errorEan);
      return;
    }

    this.stockSelected = reg;
    this.moverForm.patchValue({
      ean: reg.articuloEan || '',
      cantidad: maxDisponible
    });

    // Actualizar validadores de cantidad
    this.moverForm.get('cantidad')?.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(maxDisponible)
    ]);
    this.moverForm.get('cantidad')?.updateValueAndValidity();

    // Si ya había ubicación destino, revalidar compatibilidad de cliente
    if (this.moverForm.get('ubiDestino')?.value?.length === 12) {
      this.validarUbiDestino();
    } else {
      setTimeout(() => {
        this.focoUbiDestino();
      }, 250);
    }
  }

  seleccionarArticuloDeTabla(item: IStock) {
    if (!item.articuloEan) {
      this.notificacionesSrv.aviso('warning', 'Este artículo no tiene código EAN asignado y no puede ser seleccionado.');
      return;
    }
    this.moverForm.patchValue({ ean: item.articuloEan.trim() });
    this.validarEan();
  }

  toggleStockOrigen() {
    this.mostrarStockOrigen = !this.mostrarStockOrigen;
  }

  toggleStockDestino() {
    this.mostrarStockDestino = !this.mostrarStockDestino;
  }

  ajustarCantidad(delta: number) {
    if (!this.stockSelected) return;
    const max = this.getMaximoDisponible();
    const actual = parseInt(this.moverForm.get('cantidad')?.value, 10) || 0;
    const nueva = actual + delta;
    if (nueva >= 1 && nueva <= max) {
      this.moverForm.patchValue({ cantidad: nueva });
    }
  }

  setCantidadMaxima() {
    if (!this.stockSelected) return;
    this.moverForm.patchValue({ cantidad: this.getMaximoDisponible() });
  }

  getMaximoDisponible(): number {
    if (!this.stockSelected) return 0;
    return this.stockSelected.cantidadDisponible !== undefined
      ? this.stockSelected.cantidadDisponible
      : (this.stockSelected.cantidad || 0);
  }

  // =========================================================================
  // PASO 3: Ubicación de Destino
  // =========================================================================
  onUbiDestinoInput() {
    const val = this.moverForm.get('ubiDestino')?.value || '';
    if (val.length === 12) {
      this.validarUbiDestino();
    } else {
      this.ubiDestinoValida = false;
      this.stockDestinoList = [];
      this.mostrarStockDestino = false;
      this.errorDestino = '';
      this.codUbiDesDecript = '';
      this.destinoLibre = false;
    }
  }

  esClienteIncompatible(item: IStock): boolean {
    if (!this.stockSelected && !this.clienteOrigen && this.id_clienteOrigen == null) {
      return false;
    }

    const nomCliArticulo = (this.stockSelected?.clienteNombre || this.clienteOrigen || '').trim().toLowerCase();
    const idCliArticulo = this.stockSelected?.idCliente ?? (this.stockSelected as any)?.id_cliente ?? this.id_clienteOrigen;

    const idItem = item.idCliente ?? (item as any)?.id_cliente;
    const nomItem = (item.clienteNombre || '').trim().toLowerCase();

    // 1. Comparación por ID numérico si ambos disponen de él
    if (idItem != null && idCliArticulo != null) {
      return Number(idItem) !== Number(idCliArticulo);
    }

    // 2. Comparación por nombre de cliente si ambos disponen de él
    if (nomItem && nomCliArticulo) {
      return nomItem !== nomCliArticulo;
    }

    // 3. Si uno tiene cliente y el otro no
    if ((idItem != null || nomItem) && (!idCliArticulo && !nomCliArticulo)) {
      return true;
    }

    return false;
  }

  validarUbiDestino() {
    const ubiDestino = (this.moverForm.get('ubiDestino')?.value || '').trim();
    const ubiOrigen = (this.moverForm.get('ubiOrigen')?.value || '').trim();

    if (ubiDestino.length !== 12) {
      return;
    }

    this.errorDestino = '';
    this.ubiDestinoValida = false;
    this.destinoLibre = false;
    this.stockDestinoList = [];
    this.mostrarStockDestino = false;
    this.codUbiDesDecript = '';

    // No puede ser igual al origen
    if (ubiDestino === ubiOrigen) {
      this.errorDestino = 'La ubicación de destino no puede ser igual a la de origen.';
      this.moverForm.get('ubiDestino')?.setErrors({ mismaUbicacion: true });
      // this.notificacionesSrv.aviso('error', this.errorDestino);
      this.focoUbiDestino();
      return;
    }

    this.loadingDestino = true;

    this.stockSrv.getLista({ codUbi: ubiDestino }).subscribe({
      next: (resp) => {
        this.loadingDestino = false;

        if (resp.error) {
          this.errorDestino = resp.mensaje || 'La ubicación de destino no existe.';
          this.moverForm.get('ubiDestino')?.setErrors({ apiError: this.errorDestino });
          // this.notificacionesSrv.aviso('error', this.errorDestino);
          this.focoUbiDestino();
          return;
        }

        this.codUbiDesDecript = this.funcLib.CodUbiDecript(ubiDestino);
        this.stockDestinoList = resp.data || [];
        this.mostrarStockDestino = false; // Colapsado por defecto

        // Caso 1: Ubicación destino vacía (libre)
        if (this.stockDestinoList.length === 0) {
          this.destinoLibre = true;
          this.clienteDestino = '';
          this.ubiDestinoValida = true;
          this.moverForm.get('ubiDestino')?.setErrors(null);
          return;
        }

        // Caso 2: Ubicación destino con stock existente -> Validar que TODOS pertenezcan al mismo cliente
        this.destinoLibre = false;
        const nomCliArticulo = (this.stockSelected?.clienteNombre || this.clienteOrigen || '').trim();

        const itemIncompatible = this.stockDestinoList.find(destItem => this.esClienteIncompatible(destItem));

        if (itemIncompatible) {
          this.ubiDestinoValida = false;
          const clienteDestConflicto = itemIncompatible.clienteNombre || 'otro cliente';
          //  this.errorDestino = `La ubicación de destino contiene artículos del cliente "${clienteDestConflicto}". La ubicación debe estar vacía o contener solo artículos del mismo cliente.`;
          this.errorDestino = `La ubicación debe estar vacía o contener solo artículos del mismo cliente.`;
          this.moverForm.get('ubiDestino')?.setErrors({ clienteNotMatch: true });
          // this.notificacionesSrv.aviso('error', this.errorDestino);
          this.focoUbiDestino();
          return;
        }

        // Mismo cliente -> Válida
        this.clienteDestino = this.stockDestinoList[0].clienteNombre || nomCliArticulo;
        this.ubiDestinoValida = true;
        this.moverForm.get('ubiDestino')?.setErrors(null);
      },
      error: (err) => {
        this.loadingDestino = false;
        this.errorDestino = 'Error de conexión al verificar la ubicación de destino.';
        this.notificacionesSrv.aviso('error', this.errorDestino);
      }
    });
  }

  // =========================================================================
  // PASO 4 y 5: Confirmar / Cancelar Movimiento
  // =========================================================================
  puedeConfirmar(): boolean {
    return (
      this.ubiOrigenValida &&
      !!this.stockSelected &&
      this.ubiDestinoValida &&
      this.moverForm.valid &&
      !this.loadingGuardar
    );
  }

  confirmarMovimiento() {
    this.enviado = true;

    if (!this.puedeConfirmar()) {
      this.notificacionesSrv.aviso('warning', 'Por favor, completa todos los pasos y validaciones requeridos.');
      return;
    }

    // Doble verificación: la ubicación destino debe estar vacía o tener artículos del mismo cliente
    if (!this.destinoLibre && this.stockDestinoList.length > 0) {
      const itemIncompatible = this.stockDestinoList.find(destItem => this.esClienteIncompatible(destItem));
      if (itemIncompatible) {
        const clienteConflicto = itemIncompatible.clienteNombre || 'otro cliente';
        this.notificacionesSrv.aviso('error', `No se puede confirmar: La ubicación de destino contiene artículos del cliente "${clienteConflicto}". Solo se permiten ubicaciones vacías o con artículos del mismo cliente.`);
        return;
      }
    }

    const cantidad = parseInt(this.moverForm.get('cantidad')?.value, 10) || 0;
    const max = this.getMaximoDisponible();

    if (cantidad <= 0 || cantidad > max) {
      this.moverForm.get('cantidad')?.setErrors({ cantidadInvalida: true });
      this.notificacionesSrv.aviso('error', `La cantidad debe estar comprendida entre 1 y ${max}.`);
      return;
    }

    const ubiOrigen = this.moverForm.get('ubiOrigen')?.value;
    const ubiDestino = this.moverForm.get('ubiDestino')?.value;

    // Advertencia si es movimiento entre almacenes diferentes
    if (ubiOrigen.substring(0, 2) !== ubiDestino.substring(0, 2)) {
      Swal.fire({
        title: 'Movimiento entre almacenes',
        text: 'La ubicación destino pertenece a un almacén diferente al de origen. ¿Quieres continuar?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.ejecutarEnvioMovimiento();
        }
      });
    } else {
      this.ejecutarEnvioMovimiento();
    }
  }

  private ejecutarEnvioMovimiento() {
    const datos = {
      username: this.usuarioSrv.userdata.username,
      id_stock: this.stockSelected!.id,
      cantidad: this.moverForm.get('cantidad')?.value,
      ubiOrigen: this.moverForm.get('ubiOrigen')?.value,
      ubiDestino: this.moverForm.get('ubiDestino')?.value,
      facturable: '0'
    };

    this.loadingGuardar = true;

    this.stockSrv.stockMueveArticulo(datos).subscribe({
      next: (resp) => {
        this.loadingGuardar = false;

        if (resp.error) {
          this.notificacionesSrv.aviso('error', resp.mensaje || 'Error al procesar el movimiento de stock.');
        } else {
          this.notificacionesSrv.aviso('success', 'Stock movido correctamente');
          Swal.fire({
            title: 'Movimiento de Stock',
            text: resp.mensaje || 'Stock movido correctamente.',
            icon: 'success',
            confirmButtonText: 'Cerrar'
          }).then(() => {
            // Reiniciar para el siguiente movimiento sin salir
            this.cancelarMovimiento(false);
          });
        }
      },
      error: (err) => {
        this.loadingGuardar = false;
        this.notificacionesSrv.aviso('error', 'Error en el servidor al mover el stock.');
      }
    });
  }

  cancelarMovimiento(mostrarMensaje: boolean = true) {
    if (mostrarMensaje) {
      this.notificacionesSrv.aviso('info', 'Movimiento cancelado');
    }

    this.enviado = false;
    this.ubiOrigenValida = false;
    this.ubiDestinoValida = false;
    this.stockOrigenList = [];
    this.stockDestinoList = [];
    this.mostrarStockOrigen = false;
    this.mostrarStockDestino = false;
    this.stockSelected = null;
    this.clienteOrigen = '';
    this.id_clienteOrigen = null;
    this.clienteDestino = '';
    this.codUbiOriDecript = '';
    this.codUbiDesDecript = '';
    this.destinoLibre = false;
    this.errorOrigen = '';
    this.errorEan = '';
    this.errorDestino = '';

    this.moverForm.reset({
      ubiOrigen: '',
      ean: '',
      cantidad: 1,
      ubiDestino: ''
    });

    setTimeout(() => {
      this.focoUbiOrigen();
    }, 150);
  }

  resetDesdeOrigen() {
    this.ubiOrigenValida = false;
    this.ubiDestinoValida = false;
    this.stockOrigenList = [];
    this.stockDestinoList = [];
    this.mostrarStockOrigen = false;
    this.mostrarStockDestino = false;
    this.stockSelected = null;
    this.errorOrigen = '';
    this.errorEan = '';
    this.errorDestino = '';
    this.codUbiOriDecript = '';
    this.codUbiDesDecript = '';
    this.moverForm.patchValue({ ean: '', cantidad: 1, ubiDestino: '' }, { emitEvent: false });
  }

  cambiarOrigen() {
    this.resetDesdeOrigen();
    setTimeout(() => {
      this.focoUbiOrigen();
    }, 200);
  }

  cambiarArticulo() {
    this.stockSelected = null;
    this.ubiDestinoValida = false;
    this.stockDestinoList = [];
    this.mostrarStockDestino = false;
    this.errorEan = '';
    this.errorDestino = '';
    this.codUbiDesDecript = '';
    this.moverForm.patchValue({
      ean: '',
      cantidad: 1,
      ubiDestino: ''
    });
    setTimeout(() => {
      this.focoEan();
    }, 250);
  }

  volverInicio() {
    this.router.navigate(['/pda']);
  }

  // =========================================================================
  // Control de foco
  // =========================================================================
  focoUbiOrigen() {
    if (this.txtUbiOrigen) {
      this.txtUbiOrigen.nativeElement.focus();
      this.txtUbiOrigen.nativeElement.select();
    }
  }

  focoEan() {
    if (this.txtEan) {
      this.txtEan.nativeElement.focus();
      this.txtEan.nativeElement.select();
    }
  }

  focoCantidad() {
    if (this.txtCantidad) {
      this.txtCantidad.nativeElement.focus();
      this.txtCantidad.nativeElement.select();
    }
  }

  focoUbiDestino() {
    if (this.txtUbiDestino) {
      this.txtUbiDestino.nativeElement.focus();
      this.txtUbiDestino.nativeElement.select();
    }
  }

  get f() {
    return this.moverForm.controls;
  }
}
